// src/utils/colorUtils.js

/**
 * Extrae colores de un SVG utilizando DOMParser y expresiones regulares.
 * @param {string} svgString - La cadena SVG a analizar.
 * @returns {Array<string>} - Array de colores extraídos en formato hexadecimal.
 */
export const extractColorsFromSVG = (svgString) => {
    const colors = new Set();
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgString, "image/svg+xml");

    if (svgDoc.querySelector("parsererror")) {
        // console.error("Error parsing SVG");
        return Array.from(colors);
    }

    // Función para extraer colores de estilos en línea
    const extractColorsFromElement = (element) => {
        const fill = element.getAttribute('fill');
        const stroke = element.getAttribute('stroke');
        const style = element.getAttribute('style');

        if (fill && isColor(fill)) {
            colors.add(normalizeColor(fill));
        }
        if (stroke && isColor(stroke)) {
            colors.add(normalizeColor(stroke));
        }
        if (style) {
            const styleColors = parseStyleAttributes(style);
            styleColors.forEach(color => {
                if (isColor(color)) {
                    colors.add(normalizeColor(color));
                }
            });
        }
    };

    // Extraer colores de elementos con atributos fill y stroke
    const elementsWithFill = svgDoc.querySelectorAll('[fill]');
    elementsWithFill.forEach(extractColorsFromElement);

    const elementsWithStroke = svgDoc.querySelectorAll('[stroke]');
    elementsWithStroke.forEach(extractColorsFromElement);

    // Extraer colores de estilos internos dentro de etiquetas <style>
    const styleTags = svgDoc.querySelectorAll('style');
    styleTags.forEach(styleTag => {
        const cssText = styleTag.textContent;
        const colorRegex = /(?:fill|stroke)\s*:\s*(#[0-9a-fA-F]{3,8}|rgba?\([^\)]+\))/g;
        let match;
        while ((match = colorRegex.exec(cssText)) !== null) {
            const color = match[1];
            if (isColor(color)) {
                colors.add(normalizeColor(color));
            }
        }
    });

    // **ANÁLISIS ADICIONAL CON EXPRESIONES REGULARES**
    // Para capturar colores que puedan haber sido omitidos por DOMParser
    const additionalColors = extractColorsWithRegex(svgString);
    additionalColors.forEach(color => {
        if (isColor(color)) {
            colors.add(normalizeColor(color));
        }
    });

    return Array.from(colors);
};

/**
 * Analiza una cadena de atributos de estilo y extrae los valores de color.
 * @param {string} styleString - La cadena de estilo a analizar.
 * @returns {Array<string>} - Array de colores extraídos.
 */
const parseStyleAttributes = (styleString) => {
    const colors = [];
    // Separar la cadena de estilos en pares clave-valor
    const styles = styleString.split(';').map(style => style.trim()).filter(Boolean);

    styles.forEach(style => {
        const [property, value] = style.split(':').map(part => part.trim());
        if (property === 'fill' || property === 'stroke') {
            colors.push(value);
        }
    });

    return colors;
};

/**
 * Verifica si una cadena representa un color válido.
 * @param {string} str - La cadena a verificar.
 * @returns {boolean} - Verdadero si es un color válido, falso en caso contrario.
 */
const isColor = (str) => {
    const s = new Option().style;
    s.color = str;
    return s.color !== '';
};

/**
 * Normaliza un color a formato hexadecimal.
 * @param {string} color - La cadena de color a normalizar.
 * @returns {string} - Color en formato hexadecimal.
 */
const normalizeColor = (color) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = color;
    return ctx.fillStyle;
};

/**
 * Convierte una cadena de color RGB a hexadecimal.
 * @param {string} rgbString - La cadena RGB a convertir.
 * @returns {string|null} - Color en formato hexadecimal o null si no es válido.
 */
const rgbToHex = (rgbString) => {
    const rgb = rgbString.match(/\d+/g);
    if (!rgb || rgb.length < 3) return null;
    const [r, g, b] = rgb.map(Number);
    return '#' + [r, g, b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('');
};

/**
 * Extrae colores utilizando expresiones regulares como método adicional.
 * @param {string} svgString - La cadena SVG a analizar.
 * @returns {Array<string>} - Array de colores extraídos.
 */
const extractColorsWithRegex = (svgString) => {
    const colorRegex = /(?:fill|stroke)\s*:\s*(#[0-9a-fA-F]{3,8}|rgba?\([^\)]+\))/g;
    const colors = [];
    let match;
    while ((match = colorRegex.exec(svgString)) !== null) {
        colors.push(match[1]);
    }
    return colors;
};

/**
 * Calcula la luminancia de un color en formato hexadecimal.
 * @param {string} color - Color en formato hexadecimal.
 * @returns {number} - Luminancia del color.
 */
export const getLuminance = (color) => {
    // Asegurarse de que el color esté en formato hexadecimal
    if (!color.startsWith('#')) {
        const hex = rgbToHex(color);
        if (hex) {
            color = hex;
        } else {
            // Si no es posible convertir, asumir luminancia media
            return 0.5;
        }
    }

    // Quitar el hash si está presente
    color = color.replace('#', '');

    // Convertir abreviaciones de 3 caracteres a 6
    if (color.length === 3) {
        color = color.split('').map(char => char + char).join('');
    }

    const r = parseInt(color.substring(0, 2), 16) / 255;
    const g = parseInt(color.substring(2, 4), 16) / 255;
    const b = parseInt(color.substring(4, 6), 16) / 255;

    // Calcular luminancia según la fórmula estándar
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance;
};

/**
 * Determina si el fondo debe ser claro u oscuro basado en la luminancia promedio de los colores.
 * @param {Array<string>} colors - Array de colores en formato hexadecimal.
 * @returns {string} - 'light' o 'dark'.
 */
export const determineBackgroundTheme = (colors) => {
    if (colors.length === 0) return 'light'; // Default a claro si no hay colores

    const luminances = colors.map(color => getLuminance(color));

    // Calcular luminancia promedio
    const avgLuminance = luminances.reduce((a, b) => a + b, 0) / luminances.length;

    // Definir un umbral; 0.5 es un valor común
    return avgLuminance > 0.5 ? 'light' : 'dark';
};
